import { useCallback, useMemo, useRef, useState } from 'react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { formatFileSize } from '../compress/compressPdfEngine';
import { repairPdf } from './repairPdfEngine';
import PdfDropZone from '../../components/pdf/PdfDropZone';
import { PdfUploadedFile } from '../../components/pdf/PdfFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import PdfDocPreview from '../../components/pdf/PdfDocPreview';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import ToolCard from '../../components/common/ToolCard';
import {
    deriveToolStatus,
    downloadBlob,
    genericHowToSteps,
    getToolStatusText,
} from '../../toolPageUtils';
import { printBlob } from '../../../../../utils/generateDocPdf';
import { usePdfPaste } from '../../components/common/useFilePaste';

const MAX_FILE_BYTES = 500 * 1024 * 1024;

export default function RepairPdf() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [file, setFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [activeTier, setActiveTier] = useState(null);
    const [progress, setProgress] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const loadFile = useCallback((incoming) => {
        if (!incoming) return;
        if (!(incoming.type === 'application/pdf' || incoming.name.toLowerCase().endsWith('.pdf'))) {
            const msg = t('repairPdf.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }
        if (incoming.size > MAX_FILE_BYTES) {
            const msg = t('repairPdf.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }
        setFile(incoming);
        setResult(null);
        setActiveTier(null);
        setProgress(null);
        setError(null);
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied PDF straight in. This tool
    // holds one file, so a paste of several takes the first.
    usePdfPaste((files) => loadFile(files[0]));

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const dropped = e.dataTransfer.files?.[0];
        if (dropped) loadFile(dropped);
    };

    const clearFile = () => {
        setFile(null);
        setResult(null);
        setActiveTier(null);
        setProgress(null);
        setError(null);
    };

    const handleEditAgain = () => {
        setResult(null);
        setActiveTier(null);
        setProgress(null);
        setError(null);
    };

    const handleRepair = async () => {
        if (!file || processing) return;

        recordToolUsage('pdf/repair', { category: 'pdf' });

        setProcessing(true);
        setResult(null);
        setActiveTier(null);
        setProgress(null);
        setError(null);

        try {
            const repaired = await repairPdf(file, {
                onTier: (tier) => setActiveTier(tier),
                onPage: (page, total) => setProgress({ page, total }),
            });

            setResult({
                name: `${file.name.replace(/\.pdf$/i, '')}-repaired.pdf`,
                blob: repaired.blob,
                tier: repaired.tier,
            });
            notifications.show({
                title: t('common.success'),
                message: t('repairPdf.done', { tier: toBnDigits(repaired.tier) }),
                color: 'green',
            });
        } catch (err) {
            const msg = err?.message === 'password'
                ? t('repairPdf.errors.password')
                : t('repairPdf.errors.failed');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    const getProcessingStatusText = () => {
        if (!processing) return null;
        if (activeTier === 3 && progress) {
            return t('repairPdf.renderingPage', {
                page: toBnDigits(progress.page),
                total: toBnDigits(progress.total),
            });
        }
        if (activeTier) {
            return t(`repairPdf.tier${activeTier}`);
        }
        return t('repairPdf.processing');
    };

    const status = deriveToolStatus({
        hasInput: !!file,
        canRun: !!file,
        processing,
        hasResult: !!result,
        hasError: !!error,
    });

    const howToSteps = useMemo(() => genericHowToSteps(t), [t]);

    const resultMeta = result
        ? `${t('repairPdf.resultMethod', { tier: toBnDigits(result.tier) })} · ${formatFileSize(result.blob.size)}`
        : undefined;
    const processingLabel = getProcessingStatusText() || t('repairPdf.processing');
    const statusTextValue = getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('repairPdf.done', { tier: toBnDigits(result?.tier || 0) }),
        error: error || t('toolPage.statusText.error'),
    });
    const progressPercent = progress?.total
        ? Math.round((progress.page / progress.total) * 100)
        : undefined;
    const fileMeta = file ? formatFileSize(file.size) : '';

    return (
        <ToolPageLayout
            slug="pdf/repair"
            subtitleKey="repairPdf.subtitle"
            status={status}
            statusText={statusTextValue}
            controlsTitle={t('toolPage.files', { count: toBnDigits(file ? 1 : 0) })}
            showAdd={!file}
            onAdd={() => fileInputRef.current?.click()}
            howToSteps={howToSteps}
            onReset={file || result || error ? clearFile : undefined}
            primaryAction={{
                label: t('repairPdf.repair'),
                onClick: handleRepair,
                disabled: !file || processing || !!result,
                loading: processing,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        className="hidden"
                        onChange={(e) => { loadFile(e.target.files?.[0]); e.target.value = ''; }}
                    />

                    {!file ? (
                        <PdfDropZone
                            title={t('repairPdf.dropTitle')}
                            hint={t('repairPdf.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : (
                        <div className="space-y-2">
                            <PdfUploadedFile
                                name={file.name}
                                meta={fileMeta}
                                onRemove={clearFile}
                                removeLabel={t('repairPdf.removeFile')}
                            />
                        </div>
                    )}
                </>
            )}
            settings={(
                <ToolCard title={t('repairPdf.engineTitle')}>
                    <ul className="space-y-1.5 text-xs text-zinc-600 dark:text-zinc-300">
                        <li>{t('repairPdf.engine1')}</li>
                        <li>{t('repairPdf.engine2')}</li>
                        <li>{t('repairPdf.engine3')}</li>
                    </ul>
                </ToolCard>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('toolPage.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('toolPage.previewEmpty')}
                    idleHint={t('toolPage.previewEmptyHint')}
                    processingLabel={processingLabel}
                    progress={progressPercent}
                    error={error}
                    successLabel={resultMeta}
                    downloadLabel={t('repairPdf.download')}
                    onDownload={result ? () => downloadBlob(result.blob, result.name) : undefined}
                    onPrint={result ? () => printBlob(result.blob) : undefined}
                    onEditAgain={result ? handleEditAgain : undefined}
                    editAgainLabel={t('toolPage.editAgain')}
                    onAgain={result ? clearFile : undefined}
                    againLabel={t('repairPdf.startOver')}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('repairPdf.repair')} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={!file && !error}
                    pdfPreview={!!result && !processing && !error}
                >
                    {result && !processing && !error ? (
                        <PdfDocPreview source={result.blob} />
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
